import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Button, Space, Modal, Switch, 
  message, Popconfirm, Image, Card, Tag,
  Alert
} from 'antd';
import { 
  DeleteOutlined, ShopOutlined, 
  PlusOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { dropshipperAPI, API_BASE_URL } from '../api';
import ShopifyIcon from './Utils/ShopifyIcon';
import WooCommerceIcon from './Utils/WooCommerceIcon';

const getPlatformIcon = (platform) => {
  switch ((platform || '').toLowerCase()) {
    case 'shopify':
      return <ShopifyIcon />;
    case 'woocommerce':
      return <WooCommerceIcon />;
    default:
      return <ShopOutlined />;
  }
};

const renderPlatformTag = (platform) => {
  if (!platform) {
    return '-';
  }

  let style = {};
  const lowerPlatform = (platform || '').toLowerCase();
  if (lowerPlatform === 'woocommerce') {
    style = { background: '#f3e5f5', borderColor: '#9c27b0', color: '#6a1b9a' };
  } else if (lowerPlatform === 'shopify') {
    style = { backgroundColor: '#f0f9f0', borderColor: '#96bf48', color: '#2d5a1d' };
  } else {
    style = { backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: '#000000' };
  }

  return (
    <Tag
      icon={getPlatformIcon(platform)}
      style={style}
    >
      {platform.toUpperCase()}
    </Tag>
  );
};

const DropshipperProductManagement = () => {
  const navigate = useNavigate();
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '50'],
    showTotal: (total, range) => {
      if (!Array.isArray(range) || range.length < 2) {
        return `${total} products`;
      }
      return `${range[0]}-${range[1]} of ${total} products`;
    },
  });
  
  // 同步相关状态
  const [connectedStores, setConnectedStores] = useState([]);
  
  // 连接信息相关状态
  const [connectionModalVisible, setConnectionModalVisible] = useState(false);
  const [selectedProductConnections, setSelectedProductConnections] = useState([]);
  const [currentProductForConnections, setCurrentProductForConnections] = useState(null);
  const [singleProductSyncModalVisible, setSingleProductSyncModalVisible] = useState(false);
  const [singleProductToSync, setSingleProductToSync] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDeleting, setBatchDeleting] = useState(false);

  const formatStoreDate = (dateString) => {
    if (!dateString) {
      return '-';
    }
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    fetchMyProducts();
    fetchConnectedStores();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const fetchMyProducts = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await dropshipperAPI.getProducts({
        page,
        page_size: pageSize,
      });
      const responseData = response.data || {};
      const productsData = Array.isArray(responseData.data) ? responseData.data : [];
      // 按创建时间降序排序，确保最新的优先展示
      const sortedProducts = [...productsData].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setMyProducts(sortedProducts);
      setSelectedRowKeys([]);
      setPagination((prev) => ({
        ...prev,
        current: responseData.page || page,
        pageSize: responseData.page_size || pageSize,
        total: responseData.total ?? productsData.length,
      }));
    } catch (error) {
      message.error('Failed to fetch your products');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);


  const fetchConnectedStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/stores/integration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const normalizedStores = (data.data || []).map((entry) => {
          const storeData = entry.data || {};
          return {
            id: entry.id,
            dropshipper_id: entry.dropshipper_id,
            platform: entry.platform || storeData.platform,
            shop_domain: entry.shop_domain || storeData.shop_domain,
            created_at: entry.created_at,
            updated_at: entry.updated_at,
            data: storeData,
          };
        });
        setConnectedStores(normalizedStores);
      }
    } catch (error) {
      
    }
  };


  const handleRemoveProduct = async (id) => {
    try {
      await dropshipperAPI.deleteProduct(id);
      message.success('Product removed from your store');
      fetchMyProducts();
    } catch (error) {
      message.error('Failed to remove product');
    }
  };

  const handleUpdateProduct = async (id, data) => {
    try {
      await dropshipperAPI.updateProduct(id, data);
      message.success('Product updated successfully');
      fetchMyProducts();
    } catch (error) {
      message.error('Failed to update product');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    setBatchDeleting(true);
    try {
      await dropshipperAPI.batchDeleteProducts(selectedRowKeys);
      message.success('Selected products removed from your store');
      setSelectedRowKeys([]);
      fetchMyProducts();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete selected products');
    } finally {
      setBatchDeleting(false);
    }
  };

  // 计算变体数量的辅助函数
  const countVariants = (variantsData) => {
    if (!variantsData || !Array.isArray(variantsData)) {
      return 0;
    }
    return variantsData.length;
  };


  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    fetchMyProducts(current, pageSize);
  };

  const openStoreSelector = (record) => {
    setSingleProductToSync(record);
    setSingleProductSyncModalVisible(true);
  };

  const handleStoreSelection = (store) => {
    const targetProduct = singleProductToSync;
    if (!targetProduct) {
      message.error('No product selected');
      return;
    }

    if (!['shopify', 'woocommerce'].includes(store.platform)) {
      message.warning('Only Shopify and WooCommerce stores are supported');
      return;
    }

    const productId = targetProduct.product?.id;
    if (!productId) {
      message.error('Missing product information');
      return;
    }

    const query = new URLSearchParams({
      action: 'sync',
      platform: store.platform,
      shop_domain: store.shop_domain,
      dp_id: targetProduct.id,
    });

    setSingleProductSyncModalVisible(false);
    setSingleProductToSync(null);

    navigate(`/dropshipper/add-to-store/${productId}?${query.toString()}`, {
      state: {
        syncStore: {
          platform: store.platform,
          shop_domain: store.shop_domain,
          id: store.id,
        },
        dropshipperProductId: targetProduct.id,
      },
    });
  };

  // 显示连接信息
  const handleShowConnections = (record) => {
    setSelectedProductConnections(record.connected_platforms || []);
    setCurrentProductForConnections(record);
    setConnectionModalVisible(true);
  };

  // 处理未连接商品的同步
  const handleNotConnectedSync = (record) => {
    openStoreSelector(record);
  };

  // 处理断开连接
  const handleDisconnect = async (record, connectionIndex) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/dropshipper/products/${record.id}/connections/${connectionIndex}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        message.success(data.message || 'Successfully disconnected from store');
        // 刷新产品列表以显示更新的连接信息
        fetchMyProducts();
        // 更新当前对话框的连接列表
        setSelectedProductConnections(prev => 
          prev.filter((_, index) => index !== connectionIndex)
        );
      } else {
        message.error(data.detail || 'Failed to disconnect from store');
      }
    } catch (error) {
      
      message.error('Failed to disconnect from store');
    }
  };


  // 我的产品表格列
  const myProductColumns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {record.product?.images?.[0] ? (
            <div
              style={{
                width: 50,
                height: 50,
                marginRight: 12,
                borderRadius: 4,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Image
                src={`${API_BASE_URL}/products/${record.product.id}/images/${record.product.images[0].id}`}
                width={50}
                height={50}
                preview={false}
                style={{ cursor: 'pointer' }}
                imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onClick={() => navigate(`/product/${record.product.id}`)}
              />
            </div>
          ) : (
            <div style={{ 
              width: 50, 
              height: 50, 
              backgroundColor: '#f0f0f0',
              marginRight: 12,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              color: '#999',
              flexShrink: 0,
              textAlign: 'center',
            }}>
              No Image
            </div>
          )}
          <div>
            <div 
              style={{ fontWeight: 'bold', cursor: 'pointer', color: '#1890ff' }}
              onClick={() => window.open(`/product/${record.product.id}`, '_blank')}
            >
              {record.product?.name}
            </div>
            <div style={{ color: '#666', fontSize: 12 }}>
              {record.product?.category?.name}
            </div>
          </div>
        </div>
      ),
      width: '30%',
    },
    {
      title: 'Product Info',
      key: 'product_info',
      render: (_, record) => {
        const variantSource = Array.isArray(record.product?.variants)
          ? record.product.variants
          : [];
        const variantCount = variantSource.length;
        const variantTypes = record.product?.variant_types || [];
        return (
          <div>
            <Tag color="blue">{variantCount || 1} variants</Tag>
            {variantTypes.length > 0 && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Types: {variantTypes.join(', ')}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#999' }}>
              Set prices when syncing to stores.
            </div>
          </div>
        );
      },
      width: '15%',
    },
    {
      title: 'Shipping & Options',
      key: 'shipping_options',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px', marginBottom: 4 }}>
            <strong>Freight:</strong> {record.freight?.name || 'Not set'}
          </div>
          {record.customization && (
            <div style={{ fontSize: '12px', color: '#1890ff' }}>
              <strong>Custom:</strong> {record.customization.customization_name}
            </div>
          )}
        </div>
      ),
      width: '15%',
    },
    {
      title: 'Connection',
      key: 'connection',
      render: (_, record) => {
        const connections = record.connected_platforms || [];
        if (connections.length === 0) {
          return (
            <Tag 
              color="red" 
              style={{ cursor: 'pointer' }}
              onClick={() => handleNotConnectedSync(record)}
            >
              Not Connected
            </Tag>
          );
        } else {
          return (
            <Tag 
              color="green" 
              style={{ cursor: 'pointer' }}
              onClick={() => handleShowConnections(record)}
            >
              Connected to {connections.length} Store{connections.length > 1 ? 's' : ''}
            </Tag>
          );
        }
      },
      width: '15%',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Switch
          checked={record.is_active}
          onChange={(checked) => handleUpdateProduct(record.id, { is_active: checked })}
          size="small"
        />
      ),
      width: '8%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '13%',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            onClick={() => openStoreSelector(record)}
          >
            Add to My Store
          </Button>
          <Popconfirm
            title="Remove this product from your store?"
            onConfirm={() => handleRemoveProduct(record.id)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 180,
    },
  ];


  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
          My Products
        </h1>
        <Button 
          type="primary" 
          size="large"
          icon={<ShopOutlined />}
          onClick={() => navigate('/dropshipper/ch-products')}
          style={{ 
            backgroundColor: '#722ed1', 
            borderColor: '#722ed1',
            fontWeight: 'bold',
            boxShadow: '0 4px 8px rgba(114, 46, 209, 0.3)'
          }}
        >
          Products Gallery
        </Button>
      </div>

      {/* 我的产品 */}
      <Card 
        title="My Products" 
        style={{ marginBottom: 24 }}
      >
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} products?`}
              okText="Delete"
              cancelText="Cancel"
              icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
              okButtonProps={{ danger: true, loading: batchDeleting }}
              onConfirm={handleBatchDelete}
            >
              <Button danger loading={batchDeleting}>
                Batch Delete ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          </div>
        )}
        <Table
          columns={myProductColumns}
          dataSource={myProducts}
          rowKey="id"
          rowSelection={rowSelection}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          locale={{
            emptyText: 'No products in your store yet.'
          }}
          size="middle"
        />
      </Card>




      {/* Connection Details Modal */}
      <Modal
        title="Product Connections"
        open={connectionModalVisible}
        onCancel={() => setConnectionModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setConnectionModalVisible(false)}>
            Close
          </Button>
        ]}
        width={900}
      >
        {selectedProductConnections.length === 0 ? (
          <Alert
            message="No Connections"
            description="This product is not connected to any external stores."
            type="info"
            showIcon
          />
        ) : (
          <Table
            dataSource={selectedProductConnections.map((conn, index) => ({ ...conn, index }))}
            rowKey={(record, index) => `${record.platform}-${record.shop_domain}-${index}`}
            pagination={false}
            columns={[
              {
                title: 'Platform',
                dataIndex: 'platform',
                key: 'platform',
                render: (platform) => renderPlatformTag(platform),
                width: 160,
              },
              {
                title: 'Store',
                dataIndex: 'shop_domain',
                key: 'shop_domain',
                render: (domain) => (
                  <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">
                    {domain}
                  </a>
                ),
              },
              {
                title: 'Product Title',
                dataIndex: 'title',
                key: 'title',
                ellipsis: true,
              },
              {
                title: 'External ID',
                dataIndex: 'external_id',
                key: 'external_id',
                width: 120,
              },
              {
                title: 'Product URL',
                key: 'external_url',
                render: (_, record) => record.external_url ? (
                  <a href={record.external_url} target="_blank" rel="noopener noreferrer">
                    View Product
                  </a>
                ) : (
                  <span style={{ color: '#999' }}>No URL</span>
                ),
                width: 100,
              },
              {
                title: 'Action',
                key: 'action',
                render: (_, record) => {
                  return (
                    <Popconfirm
                      title="Disconnect from Store"
                      description={
                        <div>
                          <div>Are you sure you want to disconnect this product from the store?</div>
                          <div>This will only remove the connection record and will NOT delete the product from the third-party platform.</div>
                        </div>
                      }
                      onConfirm={() => handleDisconnect(currentProductForConnections, record.index)}
                      okText="Yes"
                      cancelText="No"
                      okButtonProps={{ danger: true }}
                    >
                      <Button 
                        type="primary" 
                        danger 
                        size="small"
                      >
                        Disconnect
                      </Button>
                    </Popconfirm>
                  );
                },
                width: 120,
              },
            ]}
          />
        )}
      </Modal>

      {/* Single Product Sync Modal */}
      <Modal
        title={`Connect "${singleProductToSync?.product?.name}" to My Stores`}
        open={singleProductSyncModalVisible}
        onCancel={() => {
          setSingleProductSyncModalVisible(false);
          setSingleProductToSync(null);
        }}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Select a store to connect this product to:</p>
        </div>
        
        {connectedStores.length === 0 ? (
          <Alert
            message="No Connected Stores"
            description="You need to connect a Shopify store first before you can connect products."
            type="warning"
            showIcon
          />
        ) : (
          <Table
            dataSource={connectedStores}
            rowKey={(record) => record.id ?? `${record.platform}-${record.shop_domain}`}
            pagination={false}
            columns={[
              {
                title: 'Platform',
                dataIndex: 'platform',
                key: 'platform',
                render: (platform) => renderPlatformTag(platform),
                width: 100,
              },
              {
                title: 'Store Domain',
                dataIndex: 'shop_domain',
                key: 'shop_domain',
                render: (domain) => (
                  <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">
                    {domain}
                  </a>
                ),
              },
              {
                title: 'Connected At',
                key: 'installed_at',
                render: (_, record) => formatStoreDate(record?.data?.installed_at),
                width: 120,
              },
              {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={() => handleStoreSelection(record)}
                    disabled={!['shopify', 'woocommerce'].includes(record.platform)}
                  >
                    {['shopify', 'woocommerce'].includes(record.platform) ? 'Add to Store' : 'Not Supported'}
                  </Button>
                ),
                width: 120,
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default DropshipperProductManagement;
